import React, { FC } from 'react';
import { getRefrigerantBandDisplay } from '../../utils/RefrigerantUtils';

interface RefrigerantBandDisplayProps {
	/** `Compliant` | `Watch` | `Exceeded`, or absent. */
	band?: string;
	style?: any;
}

/**
 * The compliance band pill.
 *
 * Same shape as every other `*StatusDisplay` in the repo — a span at `4px 12px` / `borderRadius 16`
 * with a per-status text and background colour — so a band reads like any other status in the
 * product rather than inventing a new visual language for one module.
 *
 * An absent band renders "Not banded" rather than nothing, because the two things it can mean (under
 * the 50 lb scope floor, or an appliance category no rule covers) are both statements about the
 * appliance. What it must never render is "Compliant": that is a conclusion, and a band the server
 * left empty is precisely the case where no rule supports one.
 */
export const RefrigerantBandDisplay: FC<RefrigerantBandDisplayProps> = ({ band, style = {} }) => {
	const display = getRefrigerantBandDisplay(band);
	return (
		// Only the band colours stay inline — they are the genuinely dynamic part. The pill's chrome
		// (inline-flex, no wrapping, padding, fully rounded) is fixed, so it belongs in Tailwind. `style`
		// is still spread last so a caller's override wins, as it did before.
		<span
			className="footnoteregular inline-flex whitespace-nowrap rounded-full px-3 py-1"
			style={{ color: display.color, background: display.backgroundColor, ...style }}
		>
			{display.label}
		</span>
	);
};

export default RefrigerantBandDisplay;
